/**
 * Color presets: a primary/secondary pair applied to BOTH the light and dark schemes.
 * The light/dark toggle is independent — every preset has a light and a dark rendition.
 * "Graphite" is the default: the grey-and-black Zuul wore before theming existed, expressed
 * as a fully achromatic preset (derivePalette skips the hue tint for it).
 *
 * The primary's hue drives every derived surface (see `derivePalette`), so the primaries are
 * spread around the color wheel — roughly 30-70 degrees apart. Presets that shared a hue family
 * produced backgrounds that were indistinguishable from each other, which is what the spread
 * is here to prevent. Secondaries are accents only, but each preset keeps a distinct one —
 * two presets sharing an accent read as the same theme wherever the primary is off screen.
 */
export interface PresetColors {
  primary: string;
  secondary: string;
  /**
   * Accent for the Zuul logo's two strokes (the eyes); falls back to `secondary`. Lets a preset
   * give the mark its own accent — Zuul Original's red eyes — without recoloring every
   * secondary-tinted control along with it.
   */
  logoAccent?: string;
}

export interface ThemePreset {
  id: string;
  label: string;
  light: PresetColors;
  dark: PresetColors;
}

export const PRESET_STORAGE_KEY = 'zuul-theme-preset';
// Unknown stored ids (including the retired literal 'default') fall back to this preset.
export const DEFAULT_PRESET_ID = 'zuul-original';

export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    // hue ~218 at low saturation — Graphite's restraint in Sapphire's hue family. The muted
    // primary keeps every derived surface a grey-blue slate (about half Sapphire's tint), the
    // ink-and-steel look of the pre-theming Zuul console. The logo keeps the source art's red
    // eyes via logoAccent.
    id: DEFAULT_PRESET_ID,
    label: 'Zuul Original',
    light: { primary: '#3c4b64', secondary: '#475569', logoAccent: '#c92a2a' },
    dark: { primary: '#a5b4cd', secondary: '#8e9bad', logoAccent: '#ff6b6b' },
  },
  {
    // achromatic — pure greys, so derivePalette produces untinted neutrals
    id: 'graphite',
    label: 'Graphite',
    light: { primary: '#212121', secondary: '#616161' },
    dark: { primary: '#e0e0e0', secondary: '#9e9e9e' },
  },
  {
    // hue ~221, blue (the pair the theme originally shipped with)
    id: 'sapphire-slate',
    label: 'Sapphire & Slate',
    light: { primary: '#2563eb', secondary: '#5a687d' },
    dark: { primary: '#60a5fa', secondary: '#94a3b8' },
  },
  {
    // hue ~264, violet
    id: 'violet-lilac',
    label: 'Violet & Lilac',
    light: { primary: '#7a3fd1', secondary: '#9333ea' },
    dark: { primary: '#b98cfa', secondary: '#d0b0fd' },
  },
  {
    // hue ~294, orchid
    id: 'orchid-gold',
    label: 'Orchid & Gold',
    light: { primary: '#a21caf', secondary: '#96590a' },
    dark: { primary: '#e879f9', secondary: '#fbbf24' },
  },
  {
    // hue ~345, rose
    id: 'rose-sky',
    label: 'Rose & Sky',
    light: { primary: '#be123c', secondary: '#0369a1' },
    dark: { primary: '#fb7185', secondary: '#38bdf8' },
  },
  {
    // hue ~29, copper
    id: 'copper-teal',
    label: 'Copper & Teal',
    light: { primary: '#b45309', secondary: '#0f766e' },
    dark: { primary: '#fb923c', secondary: '#2dd4bf' },
  },
  {
    // hue ~99, moss — plum accent; the amber it used to carry belonged to two other presets too
    id: 'moss-plum',
    label: 'Moss & Plum',
    light: { primary: '#4a7c2f', secondary: '#8e2f7d' },
    dark: { primary: '#a3d977', secondary: '#e38fd0' },
  },
  {
    // hue ~142, emerald
    id: 'emerald-mint',
    label: 'Emerald & Mint',
    light: { primary: '#15803d', secondary: '#0f766e' },
    dark: { primary: '#4ade80', secondary: '#5eead4' },
  },
  {
    // hue ~174, teal
    id: 'teal-amber',
    label: 'Teal & Amber',
    light: { primary: '#00796b', secondary: '#96681f' },
    dark: { primary: '#4db6ac', secondary: '#e0a955' },
  },
];

export const DEFAULT_PRESET: ThemePreset = THEME_PRESETS[0]!;

export const getPreset = (id: string | null | undefined): ThemePreset =>
  THEME_PRESETS.find((preset) => preset.id === id) ?? DEFAULT_PRESET;

/** Reads the persisted preset id; unknown or unreadable values fall back to the default. */
export const readStoredPresetId = (): string => {
  try {
    return getPreset(localStorage.getItem(PRESET_STORAGE_KEY)).id;
  } catch {
    return DEFAULT_PRESET_ID;
  }
};
